import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';

export const PermissionTest: React.FC = () => {
  const { user } = useAuth();
  const [results, setResults] = useState<string[]>([]);
  const [testing, setTesting] = useState(false);

  const addResult = (result: string) => {
    setResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${result}`]);
  };

  const testPermissions = async () => {
    if (!user) {
      addResult('❌ No user found');
      return;
    }

    setTesting(true);
    setResults([]);
    
    addResult('🧪 Starting permission tests...');
    addResult(`👤 User ID: ${user.uid}`);
    addResult(`📧 Email: ${user.email}`);
    addResult(`🎭 Role: ${user.role || 'user'}`);

    try {
      // Test 1: Read groups
      addResult('📋 Testing groups read permission...');
      const groupsQuery = query(
        collection(db, 'groups'),
        where('privacy', '==', 'public'),
        where('isArchived', '==', false),
        limit(5)
      );
      const groupsSnap = await getDocs(groupsQuery);
      addResult(`✅ Groups read: ${groupsSnap.docs.length} documents found`);

      // Test 2: Read public profiles
      addResult('👥 Testing public profiles read permission...');
      const profilesQuery = query(
        collection(db, 'publicProfiles'),
        where('isPublic', '==', true),
        limit(5)
      );
      const profilesSnap = await getDocs(profilesQuery);
      addResult(`✅ Profiles read: ${profilesSnap.docs.length} documents found`);

      // Test 3: Read user memberships
      addResult('🔗 Testing memberships read permission...');
      const membershipsQuery = query(
        collection(db, 'groupMemberships'),
        where('userId', '==', user.uid),
        limit(5)
      );
      const membershipsSnap = await getDocs(membershipsQuery);
      addResult(`✅ Memberships read: ${membershipsSnap.docs.length} documents found`);

      // Test 4: Create a test group
      addResult('✍️ Testing group creation permission...');
      const testGroup = {
        name: `Test Group ${Date.now()}`,
        description: 'Test group for permission verification',
        privacy: 'public',
        category: 'General Support',
        leaderId: user.uid,
        moderators: [user.uid],
        memberCount: 1,
        postCount: 0,
        lastActivity: serverTimestamp(),
        isArchived: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const groupRef = await addDoc(collection(db, 'groups'), testGroup);
      addResult(`✅ Group creation: Successfully created group ${groupRef.id}`);

      // Test 5: Create membership
      addResult('🔗 Testing membership creation permission...');
      const membershipData = {
        groupId: groupRef.id,
        userId: user.uid,
        role: 'leader',
        joinedAt: serverTimestamp(),
        lastSeen: serverTimestamp(),
        postCount: 0,
        helpfulCount: 0,
        notifications: true,
        canPost: true,
        canInvite: true,
        warnings: 0
      };
      
      const membershipRef = await addDoc(collection(db, 'groupMemberships'), membershipData);
      addResult(`✅ Membership creation: Successfully created membership ${membershipRef.id}`);

      addResult('🎉 All permission tests passed!');

    } catch (error: any) {
      addResult(`❌ Permission test failed: ${error.code} - ${error.message}`);
      console.error('Permission test error:', error);
    }

    setTesting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Firebase Permission Test</h1>
        
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <button
            onClick={testPermissions}
            disabled={testing}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {testing ? 'Testing...' : 'Run Permission Tests'}
          </button>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Test Results</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {results.length === 0 ? (
              <p className="text-gray-500">No tests run yet. Click the button above to start testing.</p>
            ) : (
              results.map((result, index) => (
                <div key={index} className="text-sm font-mono bg-gray-50 p-2 rounded">
                  {result}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
















